import React, { useEffect, useMemo } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import useModuleAccess from '../../hooks/useModuleAccess';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

const TABS: { key: string; label: string; path: string; subModule: string }[] = [
  { key: 'group-categories', label: 'Group Categories', path: '/coa', subModule: 'group-categories' },
  { key: 'primary-groups', label: 'Primary Groups', path: '/coa/primary-groups', subModule: 'primary-groups' },
  { key: 'sub-groups', label: 'Sub Groups', path: '/coa/sub-groups', subModule: 'sub-groups' },
  { key: 'ledgers', label: 'Ledgers', path: '/coa/ledgers', subModule: 'ledgers' },
  { key: 'tax-settlement', label: 'Tax Settlement', path: '/coa/tax-settlement', subModule: 'tax-settlement' },
  { key: 'expense-settlement', label: 'Expense Settlement', path: '/coa/expense-settlement', subModule: 'expense-settlement' },
  { key: 'statutory-compliance', label: 'Statutory Compliance', path: '/coa/statutory-compliance', subModule: 'statutory-compliance' },
  { key: 'cost-centres', label: 'Cost Centres', path: '/coa/cost-centres', subModule: 'cost-centres' },
];

// ?tab= values used by legacy links from elsewhere in the app.
const TAB_KEY_BY_PARAM: Record<string, string> = {
  taxes: 'tax-settlement',
  'expense-settlement': 'expense-settlement',
  'statutory-compliance': 'statutory-compliance',
  'cost-centres': 'cost-centres',
};

const ChartsOfAccount: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { can } = useModuleAccess();

  const visibleTabs = useMemo(
    () => TABS.filter(t => can('coa', 'view', t.subModule)),
    [can]
  );

  // Active tab = the tab whose path is the longest prefix of current URL.
  // /coa only matches exactly or /coa/group-categories
  const matchedValue = visibleTabs.reduce((best, tab) => {
    const isMatch = tab.path === "/coa"
      ? (pathname === "/coa" || pathname === "/coa/group-categories" || pathname === "/charts-of-account" || pathname === "/charts-of-account/group-categories")
      : (pathname.startsWith(tab.path) || pathname.startsWith(tab.path.replace('/coa', '/charts-of-account')));
    if (isMatch && tab.path.length > best.length) return tab.path;
    return best;
  }, "");

  const firstVisible = visibleTabs[0]?.path;

  // Handle legacy ?tab= query parameter redirects
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const key = tabParam ? TAB_KEY_BY_PARAM[tabParam] : undefined;
    if (key) {
      const targetPath = key === 'group-categories' ? '/coa' : `/coa/${key}`;
      navigate(targetPath, { replace: true });
    }
  }, [searchParams, navigate]);

  // Falls back to first visible tab if current tab is not accessible
  useEffect(() => {
    if (!matchedValue && firstVisible && pathname !== firstVisible) {
      navigate(firstVisible, { replace: true });
    }
  }, [matchedValue, firstVisible, pathname, navigate]);

  if (!visibleTabs.length) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" sx={{ color: '#666' }}>
          You do not have permission to view any Chart of Accounts section.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Sticky Tabs Section - Outside main container */}
      <Box sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: '#fff', borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={matchedValue || firstVisible}
          onChange={(_e, newPath) => navigate(newPath)}
          aria-label="Charts of Account tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            px: 1,
            '& .MuiTabs-indicator': {
              backgroundColor: '#059669',
              height: 3,
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              color: '#545454',
              minHeight: 48,
              '&.Mui-selected': {
                color: '#059669',
                fontWeight: 600,
              },
              '&:hover': {
                color: '#059669',
                backgroundColor: 'rgba(5, 150, 105, 0.04)',
              },
            },
          }}
        >
          {visibleTabs.map(t => (
            <Tab key={t.path} value={t.path} label={t.label} />
          ))}
        </Tabs>
      </Box>

      {/* Scrollable Content Section */}
      <Box sx={{ px: 1 }}>
        <Box sx={{ bgcolor: '#fff', minHeight: '70vh', pt: 2 }}>
          <Outlet />
        </Box>
      </Box>
    </>
  );
};

export default ChartsOfAccount;
